// Main pantry list. Shows one row per product (regardless of how many lots
// exist of that product), with quantity totals and the soonest expiry surfaced
// on the row. Tapping a row drills into /products/<id> for the full product
// view including all lots.
//
// Supports search, an "expiring soon" filter, and a group-by control
// (None / Expiry / Location / Category). Group-by choice persists in
// localStorage so the view feels stable.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import type { InventoryEntry, LowStockProduct, Product } from "../types";

// ---------- date helpers ----------

// IMPORTANT: best_before is stored as a calendar date (YYYY-MM-DD), not an
// instant in time. JavaScript's `new Date("YYYY-MM-DD")` parses that as UTC
// midnight, which then gets converted to the local timezone for display —
// shifting the date back a day for any user west of UTC. We sidestep that by
// constructing the Date with explicit year / month / day, which gives us
// midnight in the user's local timezone.
function parseLocalDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function daysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = parseLocalDate(dateStr);
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  return diff;
}

function formatBestBefore(d: string | null): string {
  if (!d) return "No expiry";
  const days = daysUntil(d)!;
  if (days < 0) return `Expired ${Math.abs(days)}d ago`;
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days <= 7) return `In ${days}d`;
  return parseLocalDate(d).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function expiryBadgeClass(d: string | null): string {
  const days = daysUntil(d);
  if (days === null)
    return "bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-400";
  if (days < 0)
    return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300";
  if (days <= 3)
    return "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300";
  if (days <= 14)
    return "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300";
  return "bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-400";
}

// ---------- summarization ----------

// One row in the inventory list = one ProductSummary, derived from the array
// of inventory lots returned by /api/inventory.
interface ProductSummary {
  product: Product;
  lots: InventoryEntry[]; // every lot of this product
  totalQuantity: number;
  // Lot most likely to be consumed next: soonest non-null best_before, with
  // null-best_before as fallback. Used for the displayed badge AND as the
  // target of the "−1" button.
  primaryLot: InventoryEntry;
  // Locations across all lots, deduped and joined for display.
  locationsLabel: string | null;
}

function summarize(entries: InventoryEntry[]): ProductSummary[] {
  const map = new Map<string, ProductSummary>();
  for (const entry of entries) {
    const key = entry.product.id;
    const cur = map.get(key);
    if (cur) {
      cur.lots.push(entry);
      cur.totalQuantity += entry.quantity;
    } else {
      map.set(key, {
        product: entry.product,
        lots: [entry],
        totalQuantity: entry.quantity,
        primaryLot: entry, // will reassign after sorting
        locationsLabel: null,
      });
    }
  }

  for (const summary of map.values()) {
    // Pick primary lot: soonest non-null best_before, else first lot. The
    // intermediate `a`/`b` variables let TypeScript narrow nullability;
    // calling `.reduce` without an explicit generic uses the no-initial
    // overload (which requires the array to be non-empty — true here since
    // every summary in the map was created from at least one lot).
    summary.primaryLot = summary.lots.reduce((best, lot) => {
      const a = best.best_before;
      const b = lot.best_before;
      if (a === null && b === null) return best;
      if (a === null) return lot;
      if (b === null) return best;
      return b < a ? lot : best;
    });
    // Build a comma-separated list of unique non-empty locations.
    const locs = new Set<string>();
    for (const lot of summary.lots) {
      if (lot.location && lot.location.trim()) locs.add(lot.location.trim());
    }
    summary.locationsLabel = locs.size > 0 ? [...locs].join(", ") : null;
  }

  // Stable order: products with the soonest expiry first; null-best_before
  // last; tiebreak alphabetical by name.
  return [...map.values()].sort((a, b) => {
    const ad = a.primaryLot.best_before;
    const bd = b.primaryLot.best_before;
    if (ad === null && bd === null)
      return a.product.name.localeCompare(b.product.name);
    if (ad === null) return 1;
    if (bd === null) return -1;
    if (ad !== bd) return ad < bd ? -1 : 1;
    return a.product.name.localeCompare(b.product.name);
  });
}

// ---------- grouping ----------

type Filter = "all" | "expiring";
type GroupBy = "none" | "expiry" | "location" | "category";

const GROUP_BY_KEY = "pantry.groupBy";

interface Group {
  sortKey: string; // controls group ordering; never shown to the user
  label: string;
  summaries: ProductSummary[];
}

function computeGroups(
  summaries: ProductSummary[],
  groupBy: GroupBy
): Group[] {
  if (groupBy === "none") return [];

  const buckets = new Map<string, Group>();
  const upsert = (sortKey: string, label: string, s: ProductSummary) => {
    const existing = buckets.get(sortKey);
    if (existing) existing.summaries.push(s);
    else buckets.set(sortKey, { sortKey, label, summaries: [s] });
  };

  for (const s of summaries) {
    if (groupBy === "expiry") {
      const days = daysUntil(s.primaryLot.best_before);
      if (days === null) upsert("4-none", "No expiry", s);
      else if (days < 0) upsert("0-expired", "Expired", s);
      else if (days <= 7) upsert("1-week", "Expiring this week", s);
      else if (days <= 30) upsert("2-month", "Expiring this month", s);
      else upsert("3-later", "Later", s);
    } else if (groupBy === "location") {
      // For products with lots in multiple locations, group by the primary
      // lot's location. The full set of locations is still shown on the row.
      const loc = (s.primaryLot.location || "").trim();
      const key = loc ? `1-${loc.toLowerCase()}` : "9-none";
      upsert(key, loc || "Unsorted (no location)", s);
    } else {
      // category
      const cat = (s.product.category || "").trim();
      const key = cat ? `1-${cat.toLowerCase()}` : "9-none";
      upsert(key, cat || "Unsorted (no category)", s);
    }
  }

  return [...buckets.values()].sort((a, b) =>
    a.sortKey.localeCompare(b.sortKey)
  );
}

// ---------- main component ----------

export function Inventory() {
  const [items, setItems] = useState<InventoryEntry[]>([]);
  const [lowStock, setLowStock] = useState<LowStockProduct[]>([]);
  // Product IDs already on the active grocery list. Used to hide an out-of-
  // stock row once the user has added it, so they can't accidentally add it
  // a second time.
  const [groceryProductIds, setGroceryProductIds] = useState<Set<string>>(
    new Set()
  );
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  // Category / location narrowing. Deliberately NOT persisted — it's a
  // transient "show me what's in the fridge" action, unlike group-by.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [groupBy, setGroupBy] = useState<GroupBy>(() => {
    const saved = localStorage.getItem(GROUP_BY_KEY);
    if (saved === "expiry" || saved === "location" || saved === "category") {
      return saved;
    }
    return "none";
  });

  useEffect(() => {
    localStorage.setItem(GROUP_BY_KEY, groupBy);
  }, [groupBy]);

  // `silent` skips the full-page "Loading…" swap — used by in-place mutations
  // (−1, star, add-to-list) so reconciling against the server doesn't blow away
  // <main>'s scroll position. (The scroll container is <main>; replacing the
  // list with a short spinner clamps its scrollTop to 0.)
  async function load(opts?: { silent?: boolean }) {
    if (!opts?.silent) setLoading(true);
    setErr(null);
    try {
      // Always fetch ALL lots. The "expiring soon" filter is applied client-
      // side over product summaries so a product with both an expiring lot
      // and a future lot still shows full info, not just the expiring slice.
      const params: Parameters<typeof api.listInventory>[0] = {};
      if (q.trim()) params.q = q.trim();
      const [{ items }, ls, g] = await Promise.all([
        api.listInventory(params),
        api.listLowStock().catch(() => ({ items: [] })),
        api.listGrocery(false).catch(() => ({ items: [] })),
      ]);
      setItems(items);
      setLowStock(ls.items);
      setGroceryProductIds(
        new Set(
          g.items
            .map((i) => i.product_id)
            .filter((x): x is string => !!x)
        )
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleStar(productId: string, currentlyStarred: boolean) {
    try {
      await api.updateProduct(productId, { is_starred: !currentlyStarred });
      await load({ silent: true });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
    }
  }

  async function handleAddToList(productId: string, name: string) {
    try {
      await api.addGroceryItem({ product_id: productId, name });
      await load({ silent: true });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to add to list");
    }
  }

  async function handleAddAllOutOfStock() {
    try {
      for (const p of outOfStockItems) {
        await api.addGroceryItem({ product_id: p.id, name: p.name });
      }
      await load({ silent: true });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to add to list");
    }
  }

  async function handleConsume(lotId: string) {
    // Optimistically reflect the −1 in the local list so the row updates
    // instantly without a re-render that resets scroll; then reconcile
    // silently (low-stock / out-of-stock / grocery sets may have shifted).
    setItems((prev) =>
      prev.flatMap((e) =>
        e.id !== lotId
          ? [e]
          : e.quantity > 1
          ? [{ ...e, quantity: e.quantity - 1 }]
          : [] // last unit consumed → lot is gone
      )
    );
    try {
      await api.consumeLot(lotId, 1);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
    }
    await load({ silent: true });
  }

  // Reload when search changes (debounced via the deps); filter is purely
  // client-side now so it doesn't trigger a reload.
  useEffect(() => {
    const t = setTimeout(() => void load(), 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const allSummaries = useMemo(() => summarize(items), [items]);

  // Filter dropdown options, derived from what's actually in stock so they
  // only ever list categories / locations the user has something in.
  const availableCategories = useMemo(() => {
    const set = new Set<string>();
    for (const s of allSummaries) {
      const c = (s.product.category ?? "").trim();
      if (c) set.add(c);
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [allSummaries]);

  const availableLocations = useMemo(() => {
    const set = new Set<string>();
    for (const s of allSummaries) {
      for (const lot of s.lots) {
        const l = (lot.location ?? "").trim();
        if (l) set.add(l);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [allSummaries]);

  // If the thing you filtered to is no longer in stock (e.g. you consumed the
  // last of it), drop the now-pointless filter rather than show an empty list.
  useEffect(() => {
    if (categoryFilter && !availableCategories.includes(categoryFilter))
      setCategoryFilter("");
  }, [availableCategories, categoryFilter]);
  useEffect(() => {
    if (locationFilter && !availableLocations.includes(locationFilter))
      setLocationFilter("");
  }, [availableLocations, locationFilter]);

  const hasFilterableFields =
    availableCategories.length > 0 || availableLocations.length > 0;
  const activeFilterCount = (categoryFilter ? 1 : 0) + (locationFilter ? 1 : 0);

  function clearFilters() {
    setCategoryFilter("");
    setLocationFilter("");
  }

  const visibleSummaries = useMemo(() => {
    let list = allSummaries;
    if (filter === "expiring") {
      // Products whose primary (soonest) lot is already expired or expires
      // within 14 days.
      list = list.filter((s) => {
        const days = daysUntil(s.primaryLot.best_before);
        return days !== null && days <= 14;
      });
    }
    if (categoryFilter) {
      list = list.filter(
        (s) => (s.product.category ?? "").trim() === categoryFilter
      );
    }
    if (locationFilter) {
      list = list.filter((s) =>
        s.lots.some((lot) => (lot.location ?? "").trim() === locationFilter)
      );
    }
    return list;
  }, [allSummaries, filter, categoryFilter, locationFilter]);

  const counts = useMemo(() => {
    let expiring = 0;
    let expired = 0;
    for (const s of allSummaries) {
      const d = daysUntil(s.primaryLot.best_before);
      if (d === null) continue;
      if (d < 0) expired++;
      else if (d <= 14) expiring++;
    }
    return { expiring, expired, total: allSummaries.length };
  }, [allSummaries]);

  const groups = useMemo(
    () => computeGroups(visibleSummaries, groupBy),
    [visibleSummaries, groupBy]
  );

  const lowStockIds = useMemo(
    () => new Set(lowStock.map((p) => p.id)),
    [lowStock]
  );

  // Products that have hit zero on-hand. These won't appear in the regular
  // inventory list because /api/inventory only returns rows that have at
  // least one lot, and consuming the last unit deletes the lot. Surfacing
  // them here is what makes the "+ list" prompt visible at the moment the
  // user actually runs out.
  const outOfStockItems = useMemo(
    () =>
      lowStock.filter(
        (p) => p.total_quantity === 0 && !groceryProductIds.has(p.id)
      ),
    [lowStock, groceryProductIds]
  );

  return (
    <div className="mx-auto max-w-2xl p-4">
      <div className="mb-4 space-y-3">
        <input
          className="input"
          placeholder="Search by name, brand, or barcode"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />

        <div className="flex flex-wrap items-center gap-2">
          <FilterChip
            active={filter === "all"}
            onClick={() => setFilter("all")}
            label="All"
            count={counts.total}
          />
          <FilterChip
            active={filter === "expiring"}
            onClick={() => setFilter("expiring")}
            label="Expiring soon"
            count={counts.expired + counts.expiring}
            tone={counts.expired > 0 ? "danger" : "warn"}
          />
          <div className="ml-auto flex items-center gap-2">
            {hasFilterableFields && (
              <button
                type="button"
                onClick={() => setFiltersOpen((o) => !o)}
                aria-expanded={filtersOpen}
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                  activeFilterCount > 0
                    ? "border-brand-600 bg-brand-50 text-brand-700 dark:border-brand-500 dark:bg-brand-950/40 dark:text-brand-300"
                    : "border-stone-200 bg-white text-stone-700 hover:border-stone-300 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-300 dark:hover:border-stone-600"
                }`}
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 5h18M6 12h12M10 19h4" strokeLinecap="round" />
                </svg>
                Filter
                {activeFilterCount > 0 && (
                  <span className="rounded-full bg-brand-600 px-1.5 text-xs font-semibold text-white">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            )}
            <label htmlFor="groupBy" className="text-xs text-stone-500 dark:text-stone-400">
              Group by
            </label>
            <div className="relative">
              <select
                id="groupBy"
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as GroupBy)}
                className="appearance-none rounded-lg border border-stone-300 bg-white py-1.5 pl-2 pr-7 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-100"
              >
                <option value="none">None</option>
                <option value="expiry">Expiry</option>
                <option value="location">Location</option>
                <option value="category">Category</option>
              </select>
              <svg
                viewBox="0 0 20 20"
                aria-hidden="true"
                className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-500 dark:text-stone-400"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
        </div>

        {filtersOpen && hasFilterableFields && (
          <div className="card space-y-3 p-3">
            {availableCategories.length > 0 && (
              <div>
                <label htmlFor="catFilter" className="label">Category</label>
                <FilterSelect
                  id="catFilter"
                  value={categoryFilter}
                  onChange={setCategoryFilter}
                  allLabel="All categories"
                  options={availableCategories}
                />
              </div>
            )}
            {availableLocations.length > 0 && (
              <div>
                <label htmlFor="locFilter" className="label">Location</label>
                <FilterSelect
                  id="locFilter"
                  value={locationFilter}
                  onChange={setLocationFilter}
                  allLabel="All locations"
                  options={availableLocations}
                />
              </div>
            )}
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-sm font-medium text-brand-700 underline underline-offset-2 hover:no-underline dark:text-brand-400"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {err && (
        <div className="card mb-4 border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-200">
          {err}
        </div>
      )}

      {!loading && !q.trim() && outOfStockItems.length > 0 && (
        <section className="mb-5">
          <div className="mb-2 flex items-center gap-2">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-stone-700 dark:text-stone-300">
              <span>Out of stock</span>
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-normal text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                {outOfStockItems.length}
              </span>
            </h2>
            <button
              type="button"
              onClick={handleAddAllOutOfStock}
              className="ml-auto rounded-md bg-brand-600 px-3 py-1 text-xs font-medium text-white hover:bg-brand-700"
              aria-label="Add all out-of-stock items to grocery list"
            >
              Add all to list
            </button>
          </div>
          <ul className="space-y-2">
            {outOfStockItems.map((p) => (
              <OutOfStockRow
                key={p.id}
                product={p}
                onAddToList={() => handleAddToList(p.id, p.name)}
              />
            ))}
          </ul>
        </section>
      )}

      {loading ? (
        <div className="py-12 text-center text-stone-500 dark:text-stone-400">Loading…</div>
      ) : visibleSummaries.length === 0 ? (
        <EmptyState
          narrowed={
            q.trim() !== "" || filter === "expiring" || activeFilterCount > 0
          }
          onClearAll={() => {
            setQ("");
            setFilter("all");
            clearFilters();
          }}
        />
      ) : groupBy === "none" ? (
        <ul className="space-y-2">
          {visibleSummaries.map((s) => (
            <ProductRow
              key={s.product.id}
              summary={s}
              isLow={lowStockIds.has(s.product.id)}
              onConsume={handleConsume}
              onToggleStar={() =>
                handleToggleStar(s.product.id, s.product.is_starred)
              }
              onAddToList={() => handleAddToList(s.product.id, s.product.name)}
            />
          ))}
        </ul>
      ) : (
        <div className="space-y-5">
          {groups.map((g) => (
            <section key={g.sortKey}>
              <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-stone-700 dark:text-stone-300">
                <span>{g.label}</span>
                <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-normal text-stone-500 dark:bg-stone-800 dark:text-stone-400">
                  {g.summaries.length}
                </span>
              </h2>
              <ul className="space-y-2">
                {g.summaries.map((s) => (
                  <ProductRow
                    key={s.product.id}
                    summary={s}
                    isLow={lowStockIds.has(s.product.id)}
                    onConsume={handleConsume}
                    onToggleStar={() =>
                      handleToggleStar(s.product.id, s.product.is_starred)
                    }
                    onAddToList={() =>
                      handleAddToList(s.product.id, s.product.name)
                    }
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <Link
        to="/add"
        className="fixed bottom-20 right-4 grid h-14 w-14 place-items-center rounded-full bg-brand-600 text-white shadow-lg shadow-brand-600/30 hover:bg-brand-700"
        aria-label="Add item"
      >
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M12 5v14M5 12h14" strokeLinecap="round" />
        </svg>
      </Link>
    </div>
  );
}

// ---------- subcomponents ----------

function ProductRow({
  summary,
  isLow,
  onConsume,
  onToggleStar,
  onAddToList,
}: {
  summary: ProductSummary;
  isLow: boolean;
  onConsume: (lotId: string) => void;
  onToggleStar: () => void;
  onAddToList: () => void;
}) {
  const { product, lots, totalQuantity, primaryLot, locationsLabel } = summary;
  const starred = product.is_starred;
  const lotCount = lots.length;
  return (
    <li>
      <Link
        to={`/products/${product.id}`}
        className="card flex items-center gap-3 p-3 hover:border-stone-300 hover:shadow dark:hover:border-stone-700"
      >
        <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-md bg-stone-100 dark:bg-stone-800">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="text-2xl text-stone-300 dark:text-stone-600">🥫</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleStar();
              }}
              className={`shrink-0 p-0.5 ${
                starred
                  ? "text-amber-500 dark:text-amber-400"
                  : "text-stone-300 hover:text-amber-500 dark:text-stone-600 dark:hover:text-amber-400"
              }`}
              aria-label={starred ? "Remove star" : "Mark as essential"}
              title={starred ? "Essential — restock priority" : "Mark as essential"}
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill={starred ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                <path d="M12 2l2.39 4.84L20 7.62l-3.79 3.7.89 5.21L12 14.27l-5.1 2.26.89-5.21L4 7.62l5.61-.78L12 2z" strokeLinejoin="round" />
              </svg>
            </button>
            <p className="truncate font-medium">{product.name}</p>
            <span className="shrink-0 text-xs font-semibold text-stone-500 dark:text-stone-400">
              × {totalQuantity}
            </span>
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-stone-500 dark:text-stone-400">
            {product.brand && <span className="truncate">{product.brand}</span>}
            {locationsLabel && (
              <>
                <span className="text-stone-300 dark:text-stone-600">·</span>
                <span className="truncate">{locationsLabel}</span>
              </>
            )}
            {lotCount > 1 && (
              <>
                <span className="text-stone-300 dark:text-stone-600">·</span>
                <span>{lotCount} lots</span>
              </>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span
              className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${expiryBadgeClass(
                primaryLot.best_before
              )}`}
            >
              {formatBestBefore(primaryLot.best_before)}
            </span>
            {isLow && (
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                  starred
                    ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                    : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                }`}
              >
                {starred ? "★ Restock priority" : "Running low"}
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {isLow && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAddToList();
              }}
              className="rounded-md bg-brand-600 px-2 py-1 text-xs font-medium text-white hover:bg-brand-700"
              aria-label={`Add ${product.name} to grocery list`}
              title="Add to grocery list"
            >
              + list
            </button>
          )}
          <button
            type="button"
            aria-label="Use one (decrement quantity by 1)"
            title="Use one (consumes the soonest-expiring lot)"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void onConsume(primaryLot.id);
            }}
            className="btn-secondary px-2.5 !py-1.5 text-sm font-semibold tabular-nums"
          >
            −1
          </button>
        </div>
      </Link>
    </li>
  );
}

function OutOfStockRow({
  product,
  onAddToList,
}: {
  product: LowStockProduct;
  onAddToList: () => void;
}) {
  const starred = product.is_starred;
  return (
    <li className="card flex items-center gap-3 p-3">
      <Link
        to={`/products/${product.id}`}
        className="flex min-w-0 flex-1 items-center gap-3"
      >
        {product.image_url ? (
          <img
            src={product.image_url}
            alt=""
            className="h-12 w-12 shrink-0 rounded-md object-cover opacity-60"
          />
        ) : (
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-md bg-stone-100 text-stone-400 dark:bg-stone-800 dark:text-stone-500">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 7h18M5 7v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" strokeLinecap="round" />
            </svg>
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {starred && (
              <span className="text-amber-500 dark:text-amber-400" title="Essential" aria-label="Starred">★</span>
            )}
            <p className="truncate font-medium">{product.name}</p>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
              0 on hand · restock at {product.min_quantity}
            </span>
          </div>
        </div>
      </Link>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onAddToList();
        }}
        className="shrink-0 rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700"
        aria-label={`Add ${product.name} to grocery list`}
        title="Add to grocery list"
      >
        + list
      </button>
    </li>
  );
}

// A full-width <select> styled like `.input` but with the native chevron
// suppressed in favour of the same custom chevron the "Group by" control uses,
// so it doesn't crowd the edge of the box.
function FilterSelect({
  id,
  value,
  onChange,
  allLabel,
  options,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  allLabel: string;
  options: string[];
}) {
  return (
    <div className="relative">
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input appearance-none pr-9"
      >
        <option value="">{allLabel}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
      <svg
        viewBox="0 0 20 20"
        aria-hidden="true"
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-500 dark:text-stone-400"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
  count,
  tone = "default",
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  tone?: "default" | "warn" | "danger";
}) {
  const baseInactive =
    tone === "danger"
      ? "border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
      : tone === "warn"
      ? "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300"
      : "border-stone-200 bg-white text-stone-700 dark:border-stone-700 dark:bg-stone-900 dark:text-stone-300";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? "border-brand-600 bg-brand-600 text-white"
          : `${baseInactive} hover:border-stone-300 dark:hover:border-stone-600`
      }`}
    >
      {label} <span className={active ? "text-white/80" : "opacity-60"}>{count}</span>
    </button>
  );
}

function EmptyState({
  narrowed,
  onClearAll,
}: {
  narrowed: boolean;
  onClearAll: () => void;
}) {
  return (
    <div className="card flex flex-col items-center gap-3 px-6 py-10 text-center">
      <div className="text-4xl">{narrowed ? "🔍" : "🍞"}</div>
      {narrowed ? (
        <>
          <p className="font-medium">Nothing matches</p>
          <p className="text-sm text-stone-500 dark:text-stone-400">
            No pantry items match the current search and filters.
          </p>
          <button type="button" onClick={onClearAll} className="btn-secondary mt-2">
            Clear filters
          </button>
        </>
      ) : (
        <>
          <p className="font-medium">Pantry is empty</p>
          <p className="text-sm text-stone-500 dark:text-stone-400">
            Tap the + button to scan a barcode and add your first item.
          </p>
          <Link to="/add" className="btn-primary mt-2">
            Add an item
          </Link>
        </>
      )}
    </div>
  );
}
